import { useState, useRef } from 'react'
import { toast } from '@/ui'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { validFileSize } from '@/helpers/general-helpers'
import { Button } from '../'
import {
  FileUploadWrapper,
  FileName,
  FileLabel,
  AcceptedFileTypesLabel,
  AcceptedFileTypes,
  ClearIcon,
} from './Styles'
import Popup from '@/ui/Popup'

export interface FileUploadProps {
  value?: File
  name?: string
  accept?: string
  onSelectFile?: (file: File | null) => void
  defaultFileName?: string
  onChange?: (file: File | null) => void
  clearable?: boolean
  disabled?: boolean
  disableFileSizeCheck?: boolean
}

const FileUpload = ({
  value,
  name,
  accept = '',
  onSelectFile,
  defaultFileName,
  onChange,
  clearable,
  disabled = false,
  disableFileSizeCheck = false,
}: FileUploadProps) => {
  const { t } = useTranslation()

  const [newFile, setNewFile] = useState<File | null>(null)

  const [cleared, setCleared] = useState(false)

  const fileUpload = useRef<HTMLInputElement>(null)

  const file = newFile || value

  let fileName
  if (file instanceof File) {
    fileName = file.name
  } else if (defaultFileName && !cleared) {
    fileName = defaultFileName
  } else {
    fileName = t('No file selected')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return
    }
    const file: File & { id?: string } = e?.target?.files[0]

    if (!file || (!disableFileSizeCheck && !validFileSize(file.size, t))) {
      return
    }

    file.id = uuid()

    const extension = file ? file.name.split('.').pop() : ''

    const hasExtension = file ? file.name.includes('.') : false

    const acceptArray = accept?.split(',').map(ext => ext.replace('.', ''))

    if (
      !hasExtension ||
      (accept?.length &&
        !acceptArray?.includes((extension as string).toLowerCase()))
    ) {
      toast.error(t('Invalid file type'))
      return
    }

    if (onSelectFile) {
      onSelectFile(file)
    }

    onChange && onChange(file)
    setCleared(false)
    setNewFile(file)
  }

  const clearFile = () => {
    setCleared(true)
    if (onSelectFile) {
      onSelectFile(null)
    }
    onChange && onChange(null)
    setNewFile(null)
  }

  return (
    <div>
      <FileUploadWrapper>
        <div style={{ borderRadius: 5 }}>
          <FileLabel htmlFor={name}>
            <Button
              type="button"
              disabled={disabled}
              // attached="left"
              content={t('Select file')}
              onClick={e => {
                e?.preventDefault()
                fileUpload?.current && fileUpload?.current.click()
              }}
            />

            <FileName title={fileName}>{fileName}</FileName>
          </FileLabel>

          <input
            disabled={disabled}
            type="file"
            name={name}
            id={name}
            ref={fileUpload}
            accept={
              accept
                ? accept
                    .split(',')
                    .map(extension =>
                      extension.startsWith('.') ? extension : `.${extension}`,
                    )
                    .join(',')
                : undefined
            }
            onChange={e => {
              handleChange(e)
            }}
          />
        </div>

        {clearable && !cleared && <ClearIcon onClick={clearFile} />}
      </FileUploadWrapper>
      {accept && (
        <Popup
          hoverable
          position="right center"
          trigger={
            <AcceptedFileTypesLabel>
              {t('Hover to view accepted file types')}
            </AcceptedFileTypesLabel>
          }
          content={() => (
            <AcceptedFileTypes>
              {accept.split(',').map(ext => (
                <li key={ext}>{ext}</li>
              ))}
            </AcceptedFileTypes>
          )}
        />
      )}
    </div>
  )
}

export default FileUpload
